import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  LayoutDashboard, ShieldCheck, FileText, Image, MessageSquare, Star, 
  HelpCircle, Settings, Plus, Trash2, Edit, Check, X 
} from 'lucide-react';

const AdminDashboard = () => {
  const location = useLocation();
  const { user } = useAuth(); // Represents the logged-in admin

  const navItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/content', label: 'Content', icon: FileText },
    { path: '/admin/gallery', label: 'Gallery', icon: Image },
    { path: '/admin/blog', label: 'Blog', icon: MessageSquare },
    { path: '/admin/reviews', label: 'Reviews', icon: Star },
    { path: '/admin/faq', label: 'FAQ', icon: HelpCircle },
    { path: '/admin/users', label: 'Admins', icon: ShieldCheck }, // Changed from Users
    { path: '/admin/settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="pt-16 min-h-screen bg-[#050505]" data-testid="admin-dashboard">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 min-h-[calc(100vh-4rem)] bg-[#0A0A0A] border-r border-zinc-800 p-4 hidden lg:block">
          <div className="mb-6">
            <h2 className="font-outfit text-xl font-bold text-white">Admin Panel</h2>
            <p className="text-sm text-zinc-500">Welcome, {user?.name || 'Admin'}</p>
          </div>
          <nav className="space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path || (item.path !== '/admin' && location.pathname.startsWith(item.path));
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                    isActive
                      ? 'bg-red-600 text-white'
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Mobile Nav */}
        <div className="lg:hidden w-full bg-[#0A0A0A] border-b border-zinc-800 p-2 overflow-x-auto">
          <div className="flex gap-2">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path || (item.path !== '/admin' && location.pathname.startsWith(item.path));
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-4 py-2 rounded-sm whitespace-nowrap transition-colors ${
                    isActive
                      ? 'bg-red-600 text-white'
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </Link>
              );
            })}
          </div>
        </div>

        {/* Main Content */}
        <main className="flex-1 p-6">
          <Routes>
            <Route index element={<DashboardOverview />} />
            <Route path="content" element={<ContentManager />} />
            <Route path="gallery" element={<GalleryManager />} />
            <Route path="blog" element={<BlogManager />} />
            <Route path="reviews" element={<ReviewsManager />} />
            <Route path="faq" element={<FAQManager />} />
            <Route path="users" element={<UsersManager />} />
            <Route path="settings" element={<SettingsManager />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

// Dashboard Overview
const DashboardOverview = () => {
  const [stats, setStats] = useState({
    admins: 0,
    content: 0,
    reviews: 0,
    blogs: 0,
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [usersRes, contentRes, reviewsRes, blogsRes] = await Promise.all([
          axios.get(`${API}/admin/users`, { withCredentials: true }),
          axios.get(`${API}/content`),
          axios.get(`${API}/reviews/all`, { withCredentials: true }),
          axios.get(`${API}/blog`),
        ]);
        setStats({
          admins: usersRes.data.filter(u => u.role === 'admin').length, // Only count active admins
          content: contentRes.data.length,
          reviews: reviewsRes.data.length,
          blogs: blogsRes.data.length,
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };
    fetchStats();
  }, []);

  return (
    <div>
      <h1 className="font-outfit text-3xl font-bold text-white mb-6">Dashboard Overview</h1>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Admins', value: stats.admins, icon: ShieldCheck },
          { label: 'Content', value: stats.content, icon: FileText },
          { label: 'Reviews', value: stats.reviews, icon: Star },
          { label: 'Blogs', value: stats.blogs, icon: MessageSquare },
        ].map((stat, idx) => (
          <div key={idx} className="card-hover bg-[#121212] p-6 rounded-sm">
            <stat.icon className="w-8 h-8 text-red-500 mb-3" />
            <div className="font-outfit text-3xl font-bold text-white">{stat.value}</div>
            <div className="text-sm text-zinc-500">{stat.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Content Manager
const ContentManager = () => {
  const [content, setContent] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content_type: 'video',
    file_url: '',
    class_level: '',
    stream: '',
    is_paid: false,
    price: '',
  });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content`);
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/content/${editingId}`, formData, { withCredentials: true });
        toast.success('Content updated!');
      } else {
        await axios.post(`${API}/content`, formData, { withCredentials: true });
        toast.success('Content added!');
      }
      setShowForm(false);
      setEditingId(null);
      resetForm();
      fetchContent();
    } catch (error) {
      toast.error('Failed to save content');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await axios.delete(`${API}/content/${id}`, { withCredentials: true });
      toast.success('Content deleted!');
      fetchContent();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleEdit = (item) => {
    setFormData({
      title: item.title,
      description: item.description,
      content_type: item.content_type,
      file_url: item.file_url,
      class_level: item.class_level || '',
      stream: item.stream || '',
      is_paid: item.is_paid,
      price: item.price || '',
    });
    setEditingId(item.content_id);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      content_type: 'video',
      file_url: '',
      class_level: '',
      stream: '',
      is_paid: false,
      price: '',
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="font-outfit text-3xl font-bold text-white">Content Management</h1>
        <Button
          onClick={() => { setShowForm(true); setEditingId(null); resetForm(); }}
          className="bg-red-600 hover:bg-red-700 text-white rounded-sm"
          data-testid="add-content-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Content
        </Button>
      </div>

      {showForm && (
        <div className="card-hover bg-[#121212] p-6 rounded-sm mb-6">
          <h2 className="font-outfit text-xl font-bold text-white mb-4">
            {editingId ? 'Edit Content' : 'Add Content'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Title *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="bg-[#0A0A0A] border-zinc-800 text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Type *</label>
                <select
                  value={formData.content_type}
                  onChange={(e) => setFormData({ ...formData, content_type: e.target.value })}
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-zinc-800 rounded-sm text-white"
                >
                  <option value="video">Video</option>
                  <option value="pdf">PDF</option>
                  <option value="ppt">PPT</option>
                  <option value="image">Image</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Description *</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                className="bg-[#0A0A0A] border-zinc-800 text-white"
                maxLength={100}
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-2">File URL *</label>
              <Input
                value={formData.file_url}
                onChange={(e) => setFormData({ ...formData, file_url: e.target.value })}
                required
                className="bg-[#0A0A0A] border-zinc-800 text-white"
                placeholder="https://..."
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Class Level</label>
                <select
                  value={formData.class_level}
                  onChange={(e) => setFormData({ ...formData, class_level: e.target.value })}
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-zinc-800 rounded-sm text-white"
                >
                  <option value="">Select Class</option>
                  {['6', '7', '8', '9', '10', '11', '12'].map((c) => (
                    <option key={c} value={c}>Class {c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Stream</label>
                <select
                  value={formData.stream}
                  onChange={(e) => setFormData({ ...formData, stream: e.target.value })}
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-zinc-800 rounded-sm text-white"
                >
                  <option value="">Select Stream</option>
                  <option value="commerce">Commerce</option>
                  <option value="science">Science</option>
                  <option value="biology">Biology</option>
                  <option value="humanities">Humanities</option>
                </select>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-zinc-400">
                <input
                  type="checkbox"
                  checked={formData.is_paid}
                  onChange={(e) => setFormData({ ...formData, is_paid: e.target.checked })}
                  className="rounded"
                />
                Paid Content
              </label>
              {formData.is_paid && (
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="Price (₹)"
                  className="w-32 bg-[#0A0A0A] border-zinc-800 text-white"
                />
              )}
            </div>
            <div className="flex gap-3">
              <Button type="submit" className="bg-red-600 hover:bg-red-700 text-white rounded-sm">
                {editingId ? 'Update' : 'Add'} Content
              </Button>
              <Button
                type="button"
                onClick={() => { setShowForm(false); setEditingId(null); resetForm(); }}
                variant="outline"
                className="border-zinc-700 text-zinc-400 rounded-sm"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-4">
        {content.map((item) => (
          <div key={item.content_id} className="card-hover bg-[#121212] p-4 rounded-sm flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-xs px-2 py-1 rounded-sm uppercase ${
                  item.content_type === 'video' ? 'bg-red-500/10 text-red-500' :
                  item.content_type === 'pdf' ? 'bg-blue-500/10 text-blue-500' :
                  'bg-orange-500/10 text-orange-500'
                }`}>
                  {item.content_type}
                </span>
                {item.is_paid && <span className="text-xs px-2 py-1 bg-yellow-500/10 text-yellow-500 rounded-sm">₹{item.price}</span>}
              </div>
              <h3 className="font-semibold text-white">{item.title}</h3>
              <p className="text-sm text-zinc-500">{item.description}</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => handleEdit(item)} variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
                <Edit className="w-4 h-4" />
              </Button>
              <Button onClick={() => handleDelete(item.content_id)} variant="ghost" size="sm" className="text-red-400 hover:text-red-300">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Gallery Manager
const GalleryManager = () => {
  const [images, setImages] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ title: '', description: '', image_url: '' });

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setImages(response.data);
    } catch (error) {
      console.error('Error fetching gallery:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/gallery`, formData, { withCredentials: true });
      toast.success('Image added!');
      setShowForm(false);
      setFormData({ title: '', description: '', image_url: '' });
      fetchImages();
    } catch (error) {
      toast.error('Failed to add image');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await axios.delete(`${API}/gallery/${id}`, { withCredentials: true });
      toast.success('Image deleted!');
      fetchImages();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="font-outfit text-3xl font-bold text-white">Gallery Management</h1>
        <Button onClick={() => setShowForm(true)} className="bg-red-600 hover:bg-red-700 text-white rounded-sm">
          <Plus className="w-4 h-4 mr-2" /> Add Image
        </Button>
      </div>

      {showForm && (
        <div className="card-hover bg-[#121212] p-6 rounded-sm mb-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Title *</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                className="bg-[#0A0A0A] border-zinc-800 text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Description *</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                className="bg-[#0A0A0A] border-zinc-800 text-white"
                maxLength={100}
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Image URL *</label>
              <Input
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                required
                className="bg-[#0A0A0A] border-zinc-800 text-white"
              />
            </div>
            <div className="flex gap-3">
              <Button type="submit" className="bg-red-600 hover:bg-red-700 text-white rounded-sm">Add Image</Button>
              <Button type="button" onClick={() => setShowForm(false)} variant="outline" className="border-zinc-700 text-zinc-400 rounded-sm">Cancel</Button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {images.map((img) => (
          <div key={img.image_id} className="card-hover bg-[#121212] rounded-sm overflow-hidden group relative">
            <img src={img.image_url} alt={img.title} className="w-full h-32 object-cover" />
            <div className="p-3">
              <h3 className="text-sm font-semibold text-white truncate">{img.title}</h3>
            </div>
            <button
              onClick={() => handleDelete(img.image_id)}
              className="absolute top-2 right-2 p-2 bg-red-600 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Trash2 className="w-4 h-4 text-white" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// Blog Manager
const BlogManager = () => {
  const [blogs, setBlogs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ title: '', content: '', image_url: '' });

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      setBlogs(response.data);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/blog/${editingId}`, formData, { withCredentials: true });
        toast.success('Blog updated!');
      } else {
        await axios.post(`${API}/blog`, formData, { withCredentials: true });
        toast.success('Blog added!');
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({ title: '', content: '', image_url: '' });
      fetchBlogs();
    } catch (error) {
      toast.error('Failed to save blog');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await axios.delete(`${API}/blog/${id}`, { withCredentials: true });
      toast.success('Blog deleted!');
      fetchBlogs();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="font-outfit text-3xl font-bold text-white">Blog Management</h1>
        <Button onClick={() => { setShowForm(true); setEditingId(null); setFormData({ title: '', content: '', image_url: '' }); }} className="bg-red-600 hover:bg-red-700 text-white rounded-sm">
          <Plus className="w-4 h-4 mr-2" /> Add Post
        </Button>
      </div>

      {showForm && (
        <div className="card-hover bg-[#121212] p-6 rounded-sm mb-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Title *</label>
              <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required className="bg-[#0A0A0A] border-zinc-800 text-white" />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Content *</label>
              <Textarea value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} required className="bg-[#0A0A0A] border-zinc-800 text-white min-h-[200px]" />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Image URL</label>
              <Input value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} className="bg-[#0A0A0A] border-zinc-800 text-white" />
            </div>
            <div className="flex gap-3">
              <Button type="submit" className="bg-red-600 hover:bg-red-700 text-white rounded-sm">{editingId ? 'Update' : 'Add'} Post</Button>
              <Button type="button" onClick={() => setShowForm(false)} variant="outline" className="border-zinc-700 text-zinc-400 rounded-sm">Cancel</Button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-4">
        {blogs.map((blog) => (
          <div key={blog.blog_id} className="card-hover bg-[#121212] p-4 rounded-sm flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-white">{blog.title}</h3>
              <p className="text-sm text-zinc-500">By {blog.author} • {new Date(blog.created_at).toLocaleDateString()}</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => { setFormData({ title: blog.title, content: blog.content, image_url: blog.image_url || '' }); setEditingId(blog.blog_id); setShowForm(true); }} variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
                <Edit className="w-4 h-4" />
              </Button>
              <Button onClick={() => handleDelete(blog.blog_id)} variant="ghost" size="sm" className="text-red-400 hover:text-red-300">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Reviews Manager
const ReviewsManager = () => {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/all`, { withCredentials: true });
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleApprove = async (id) => {
    try {
      await axios.put(`${API}/reviews/${id}/approve`, {}, { withCredentials: true });
      toast.success('Review approved!');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await axios.delete(`${API}/reviews/${id}`, { withCredentials: true });
      toast.success('Review deleted!');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div>
      <h1 className="font-outfit text-3xl font-bold text-white mb-6">Review Approvals</h1>
      <div className="space-y-4">
        {reviews.map((review) => (
          <div key={review.review_id} className="card-hover bg-[#121212] p-4 rounded-sm">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-zinc-600'}`} />
                    ))}
                  </div>
                  {review.is_approved ? (
                    <span className="text-xs px-2 py-1 bg-green-500/10 text-green-500 rounded-sm">Approved</span>
                  ) : (
                    <span className="text-xs px-2 py-1 bg-yellow-500/10 text-yellow-500 rounded-sm">Pending</span>
                  )}
                </div>
                <p className="text-zinc-400">{review.comment}</p>
                <p className="text-sm text-zinc-500 mt-2">{review.user_name} • {new Date(review.created_at).toLocaleDateString()}</p>
              </div>
              <div className="flex gap-2">
                {!review.is_approved && (
                  <Button onClick={() => handleApprove(review.review_id)} variant="ghost" size="sm" className="text-green-400 hover:text-green-300">
                    <Check className="w-4 h-4" />
                  </Button>
                )}
                <Button onClick={() => handleDelete(review.review_id)} variant="ghost" size="sm" className="text-red-400 hover:text-red-300">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// FAQ Manager
const FAQManager = () => {
  const [faqs, setFaqs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ question: '', answer: '', order: 0 });

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      const response = await axios.get(`${API}/faq`);
      setFaqs(response.data);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/faq/${editingId}`, formData, { withCredentials: true });
        toast.success('FAQ updated!');
      } else {
        await axios.post(`${API}/faq`, formData, { withCredentials: true });
        toast.success('FAQ added!');
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({ question: '', answer: '', order: 0 });
      fetchFaqs();
    } catch (error) {
      toast.error('Failed to save FAQ');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await axios.delete(`${API}/faq/${id}`, { withCredentials: true });
      toast.success('FAQ deleted!');
      fetchFaqs();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="font-outfit text-3xl font-bold text-white">FAQ Management</h1>
        <Button onClick={() => { setShowForm(true); setEditingId(null); setFormData({ question: '', answer: '', order: faqs.length }); }} className="bg-red-600 hover:bg-red-700 text-white rounded-sm">
          <Plus className="w-4 h-4 mr-2" /> Add FAQ
        </Button>
      </div>

      {showForm && (
        <div className="card-hover bg-[#121212] p-6 rounded-sm mb-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Question *</label>
              <Input value={formData.question} onChange={(e) => setFormData({ ...formData, question: e.target.value })} required className="bg-[#0A0A0A] border-zinc-800 text-white" />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Answer *</label>
              <Textarea value={formData.answer} onChange={(e) => setFormData({ ...formData, answer: e.target.value })} required className="bg-[#0A0A0A] border-zinc-800 text-white min-h-[100px]" />
            </div>
            <div className="flex gap-3">
              <Button type="submit" className="bg-red-600 hover:bg-red-700 text-white rounded-sm">{editingId ? 'Update' : 'Add'} FAQ</Button>
              <Button type="button" onClick={() => setShowForm(false)} variant="outline" className="border-zinc-700 text-zinc-400 rounded-sm">Cancel</Button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-4">
        {faqs.map((faq) => (
          <div key={faq.faq_id} className="card-hover bg-[#121212] p-4 rounded-sm">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-white mb-1">{faq.question}</h3>
                <p className="text-sm text-zinc-400">{faq.answer}</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => { setFormData({ question: faq.question, answer: faq.answer, order: faq.order }); setEditingId(faq.faq_id); setShowForm(true); }} variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
                  <Edit className="w-4 h-4" />
                </Button>
                <Button onClick={() => handleDelete(faq.faq_id)} variant="ghost" size="sm" className="text-red-400 hover:text-red-300">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Users (Admins) Manager
const UsersManager = () => {
  const [users, setUsers] = useState([]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching admin users:', error);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    if (newRole === 'user' && !window.confirm('Are you sure you want to revoke Admin access for this account?')) return;
    
    try {
      await axios.put(`${API}/admin/users/${userId}/role?role=${newRole}`, {}, { withCredentials: true });
      toast.success(newRole === 'user' ? 'Admin access revoked' : 'Role updated to Admin');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  return (
    <div>
      <h1 className="font-outfit text-3xl font-bold text-white mb-6">Manage Access</h1>
      <div className="space-y-4">
        {users.map((user) => (
          <div key={user.user_id} className="card-hover bg-[#121212] p-4 rounded-sm flex items-center justify-between">
            <div className="flex items-center gap-4">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-zinc-400" />
                </div>
              )}
              <div>
                <h3 className="font-semibold text-white">{user.name}</h3>
                <p className="text-sm text-zinc-500">{user.email}</p>
              </div>
            </div>
            <select
              value={user.role}
              onChange={(e) => handleRoleChange(user.user_id, e.target.value)}
              className={`px-3 py-2 bg-[#0A0A0A] border border-zinc-800 rounded-sm text-white ${user.role === 'admin' ? 'border-red-500' : ''}`}
            >
              <option value="admin">Admin</option>
              <option value="user">No Access</option>
            </select>
          </div>
        ))}
      </div>
    </div>
  );
};

// Settings Manager
const SettingsManager = () => {
  const [settings, setSettings] = useState({
    site_name: '',
    hero_title: '',
    hero_subtitle: '',
    hero_image_url: '',
    about_content: '',
    owner_name: '',
    owner_photo: '',
    footer_content: '',
    logo_url: '',
    favicon_url: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings, { withCredentials: true });
      toast.success('Settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h1 className="font-outfit text-3xl font-bold text-white mb-6">Global Settings</h1>
      <div className="card-hover bg-[#121212] p-6 rounded-sm space-y-6">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Site Name</label>
            <Input value={settings.site_name} onChange={(e) => setSettings({ ...settings, site_name: e.target.value })} className="bg-[#0A0A0A] border-zinc-800 text-white" />
          </div>
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Owner Name</label>
            <Input value={settings.owner_name} onChange={(e) => setSettings({ ...settings, owner_name: e.target.value })} className="bg-[#0A0A0A] border-zinc-800 text-white" />
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Logo URL</label>
            <Input value={settings.logo_url || ''} onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })} className="bg-[#0A0A0A] border-zinc-800 text-white" />
          </div>
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Favicon URL</label>
            <Input value={settings.favicon_url || ''} onChange={(e) => setSettings({ ...settings, favicon_url: e.target.value })} className="bg-[#0A0A0A] border-zinc-800 text-white" />
          </div>
        </div>
        <div>
          <label className="block text-sm text-zinc-400 mb-2">Hero Title</label>
          <Input value={settings.hero_title} onChange={(e) => setSettings({ ...settings, hero_title: e.target.value })} className="bg-[#0A0A0A] border-zinc-800 text-white" />
        </div>
        <div>
          <label className="block text-sm text-zinc-400 mb-2">Hero Subtitle</label>
          <Input value={settings.hero_subtitle} onChange={(e) => setSettings({ ...settings, hero_subtitle: e.target.value })} className="bg-[#0A0A0A] border-zinc-800 text-white" />
        </div>
        <div>
          <label className="block text-sm text-zinc-400 mb-2">Hero Image URL</label>
          <Input value={settings.hero_image_url || ''} onChange={(e) => setSettings({ ...settings, hero_image_url: e.target.value })} className="bg-[#0A0A0A] border-zinc-800 text-white" />
        </div>
        <div>
          <label className="block text-sm text-zinc-400 mb-2">Owner Photo URL</label>
          <Input value={settings.owner_photo || ''} onChange={(e) => setSettings({ ...settings, owner_photo: e.target.value })} className="bg-[#0A0A0A] border-zinc-800 text-white" />
        </div>
        <div>
          <label className="block text-sm text-zinc-400 mb-2">About Content</label>
          <Textarea value={settings.about_content} onChange={(e) => setSettings({ ...settings, about_content: e.target.value })} className="bg-[#0A0A0A] border-zinc-800 text-white min-h-[200px]" />
        </div>
        <div>
          <label className="block text-sm text-zinc-400 mb-2">Footer Content</label>
          <Textarea value={settings.footer_content} onChange={(e) => setSettings({ ...settings, footer_content: e.target.value })} className="bg-[#0A0A0A] border-zinc-800 text-white" />
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white rounded-sm">
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};

export default AdminDashboard;
